import * as THREE from 'three';
import { InputManager } from './InputManager';

export class Player {
  public camera: THREE.PerspectiveCamera;
  public position: THREE.Vector3;
  public velocity: THREE.Vector3;
  
  private inputManager: InputManager;
  private pitch: number = 0;
  private yaw: number = 0;
  
  // Movement settings
  public baseSpeed: number = 5;
  public sprintMultiplier: number = 1.5;
  public speedMultiplier: number = 1; // Modified by masks
  
  // Physics
  private gravity: number = -20;
  private jumpForce: number = 8;
  private isGrounded: boolean = true;
  private playerHeight: number = 1.7;
  private playerRadius: number = 0.3;
  
  // State
  public isInWater: boolean = false;
  public canBreatheUnderwater: boolean = false;
  public isInvisible: boolean = false;
  public canPhaseThrough: boolean = false;
  public hasLightAura: boolean = false;
  
  // Collision detection callback
  public checkCollision: ((position: THREE.Vector3, radius: number) => THREE.Vector3 | null) | null = null;
  public checkWater: ((position: THREE.Vector3) => boolean) | null = null;
  
  constructor(inputManager: InputManager) {
    this.inputManager = inputManager;
    this.position = new THREE.Vector3(0, this.playerHeight, 0);
    this.velocity = new THREE.Vector3();
    
    this.camera = new THREE.PerspectiveCamera(
      75, // FOV
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    
    this.camera.position.copy(this.position);
    
    // Handle window resize
    window.addEventListener('resize', () => this.onWindowResize());
  }
  
  private onWindowResize(): void {
    this.camera.aspect = window.innerWidth / window.innerHeight;
    this.camera.updateProjectionMatrix();
  }
  
  public update(deltaTime: number, timeScale: number = 1): void {
    // Apply time scale to delta
    const scaledDelta = deltaTime * timeScale;
    
    // Handle mouse look
    if (this.inputManager.pointerLocked) {
      const mouseMovement = this.inputManager.getMouseMovement();
      const sensitivity = 0.002;
      
      this.yaw -= mouseMovement.x * sensitivity;
      this.pitch -= mouseMovement.y * sensitivity;
      
      // Clamp pitch
      this.pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, this.pitch));
    }
    
    // Update camera rotation
    this.camera.rotation.order = 'YXZ';
    this.camera.rotation.y = this.yaw;
    this.camera.rotation.x = this.pitch;
    
    // Get movement input
    const movement = this.inputManager.getMovementVector();
    
    // Calculate movement direction relative to camera
    const forward = new THREE.Vector3(0, 0, -1);
    const right = new THREE.Vector3(1, 0, 0);
    
    forward.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    right.applyAxisAngle(new THREE.Vector3(0, 1, 0), this.yaw);
    
    // Calculate desired velocity
    const moveDirection = new THREE.Vector3();
    moveDirection.addScaledVector(forward, -movement.z);
    moveDirection.addScaledVector(right, movement.x);
    
    // Apply speed
    let speed = this.baseSpeed * this.speedMultiplier;
    if (this.inputManager.isSprinting()) {
      speed *= this.sprintMultiplier;
    }
    
    // Slower in water
    if (this.isInWater && !this.canBreatheUnderwater) {
      speed *= 0.5;
    }
    
    // Set horizontal velocity
    this.velocity.x = moveDirection.x * speed;
    this.velocity.z = moveDirection.z * speed;
    
    // Apply gravity (reduced in water)
    if (!this.isGrounded) {
      const gravityScale = this.isInWater ? 0.3 : 1;
      this.velocity.y += this.gravity * scaledDelta * gravityScale;
    }
    
    // Swimming in water with Poseidon mask
    if (this.isInWater && this.canBreatheUnderwater) {
      if (this.inputManager.isKeyPressed('Space')) {
        this.velocity.y = 4; // Swim up
      } else if (this.inputManager.isKeyPressed('ShiftLeft') || this.inputManager.isKeyPressed('ShiftRight') || this.inputManager.isKeyPressed('KeyC')) {
        this.velocity.y = -4; // Swim down (Shift or C key)
      } else {
        this.velocity.y *= 0.85; // Damping - slowly float
      }
    }
    
    // Calculate new position
    const newPosition = this.position.clone();
    newPosition.addScaledVector(this.velocity, scaledDelta);
    
    // Collision detection
    if (this.checkCollision && !this.canPhaseThrough) {
      const correctedPosition = this.checkCollision(newPosition, this.playerRadius);
      if (correctedPosition) {
        newPosition.copy(correctedPosition);
      }
    }
    
    // Check water BEFORE ground check so we know if we can swim down
    if (this.checkWater) {
      this.isInWater = this.checkWater(newPosition);
    }
    
    // Ground check - but allow swimming below ground level if in water with Poseidon mask
    const canSwimDown = this.isInWater && this.canBreatheUnderwater;
    const minY = canSwimDown ? -10 : this.playerHeight; // Allow diving up to 10 units below
    
    if (newPosition.y < minY) {
      newPosition.y = minY;
      this.velocity.y = 0;
      this.isGrounded = !canSwimDown;
    } else if (newPosition.y < this.playerHeight && !canSwimDown) {
      newPosition.y = this.playerHeight;
      this.velocity.y = 0;
      this.isGrounded = true;
    } else {
      this.isGrounded = false;
    }
    
    // Jump (only when grounded and not in water)
    if (this.isGrounded && this.inputManager.isKeyPressed('Space') && !this.isInWater) {
      this.velocity.y = this.jumpForce;
      this.isGrounded = false;
    }
    
    // Apply position
    this.position.copy(newPosition);
    this.camera.position.copy(this.position);
  }
  
  public teleportTo(x: number, y: number, z: number): void {
    this.position.set(x, y + this.playerHeight, z);
    this.velocity.set(0, 0, 0);
    this.camera.position.copy(this.position);
  }
  
  public lookAt(target: THREE.Vector3): void {
    const direction = target.clone().sub(this.position);
    this.yaw = Math.atan2(-direction.x, -direction.z);
    this.pitch = Math.atan2(direction.y, Math.sqrt(direction.x * direction.x + direction.z * direction.z));
  }
}
